import { useParams, usePathname } from 'next/navigation';

// Utils
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useActiveTreeStructure from '@/utils/hooks/tree-structure/useActiveTreeStructure';
import useDisplayFirstStatsPanel from '@/utils/hooks/firstStats/useDisplayFirstStatsPanel';

// Lib
import { formatUrlKeys, useProfileHasAccessPage } from '@/utils/hooks/acl/useProfileHasAccessPage';
import { networkHasAccess } from '@/lib/acl/pageHasAccess';

// Components
import FirstStats from '@/components/composite/FirstStats';
import { GroupEmptyState } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/GroupEmptyState';
import { SocialProfileEmptyState } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/SocialProfileEmptyState';
import { MultiProfilesEmptyState } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/MultiProfilesEmptyState';

// Types
import type { SocialProfileNetworkType } from '@/types/socialProfileType';

type PageContentWrapperProps = {
    children: React.ReactNode;
};

const allNetworksAvailable: SocialProfileNetworkType[] = [
    'facebook',
    'instagram',
    'linkedin',
    'tiktok_business',
    'pinterest'
];

export function PageContentWrapper({ children }: PageContentWrapperProps) {
    const params = useParams();
    const pathname = usePathname();
    const { activeSocialProfile } = useActiveSocialProfile();
    const { isTreeStructureActivated } = useActiveTreeStructure();

    const { hideFirstStatsEmptyState, setHideFirstStatsEmptyState } = useDisplayFirstStatsPanel();

    const {
        activeSocialProfileNoAccess,
        activeGroupNoAccess,
        activeMultiSelectionProfilesNoAccess
    } = useProfileHasAccessPage();

    if (!isTreeStructureActivated) {
        return children;
    }

    const networkHasAccessFormatted = formatUrlKeys(networkHasAccess, params);
    const emptyStateName = getEmptyStateName(pathname);

    if (activeSocialProfileNoAccess) {
        return (
            <SocialProfileEmptyState
                supportedNetworkTypes={networkHasAccessFormatted[pathname]}
                emptyStateName={emptyStateName}
                socialProfileNetworkType={activeSocialProfile.social_network_type}
            />
        );
    }

    if (activeGroupNoAccess) {
        return (
            <GroupEmptyState
                supportedNetworkTypes={
                    networkHasAccessFormatted?.[pathname] ?? allNetworksAvailable
                }
                emptyStateName={emptyStateName}
            />
        );
    }

    if (activeMultiSelectionProfilesNoAccess) {
        return (
            <MultiProfilesEmptyState
                supportedNetworkTypes={
                    networkHasAccessFormatted?.[pathname] ?? allNetworksAvailable
                }
            />
        );
    }

    if (!hideFirstStatsEmptyState) {
        return (
            <div className="overflow-hidden rounded-md bg-white dark:bg-icoDarkMode-anthracite">
                <FirstStats start={() => setHideFirstStatsEmptyState(true)} />
            </div>
        );
    }

    return children;
}

function getEmptyStateName(pathname?: string) {
    if (pathname?.includes('/analytics')) {
        return 'analytics';
    }
    if (pathname?.includes('/listening')) {
        return 'listening';
    }
    if (pathname?.includes('/campaigns')) {
        return 'content.campaigns';
    }
    return pathname?.replace('/', '').replaceAll('/', '.');
}
